export type MessageRole = 'system' | 'user' | 'assistant' | 'tool';

export interface MessageEntry {
  role: MessageRole;
  content: string;
}

export interface ChatSettings {
  temperature: number;
  maxTokens: number;
  streaming: boolean;
  stopSequences: string[];
  toolsEnabled: string[];
  apiKeysRef?: string;
}

export interface VoiceSettings {
  provider?: string;
  voiceId?: string;
  speakingRate?: number; // 0.5 - 2.0
  pitch?: number; // -20 to +20 semitones
  volume?: number; // 0 - 1
  enableStreaming?: boolean;
  language?: string;
}

export interface KnowledgeAttachment {
  id: string;
  name: string;
  type: string;
  size?: number;
  url?: string;
  createdAt?: string;
}

export interface ToolCallResult {
  toolName: string;
  success: boolean;
  result?: any;
  error?: string;
}

export interface ConversationMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: string | Date;
  toolExecutions?: ToolCallResult[];
}

export interface ToolConfiguration {
  id: string;
  name: string;
  description: string;
  category: 'web_search' | 'file_management' | 'data_analysis' | 'communication' | 'calendar' | 'crm' | 'custom_api' | 'code_execution' | 'image_generation' | 'workflow_automation';
  enabled: boolean;
  required?: boolean;
  configuration?: Record<string, any>;
  apiKey?: string;
  endpoint?: string;
  parameters?: Array<{ key: string; type: string; required: boolean; default?: any }>;
}

export interface IntegrationConfig {
  id: string;
  name: string;
  type: 'crm' | 'calendar' | 'email' | 'slack' | 'teams' | 'notion' | 'airtable' | 'zapier' | 'webhook' | 'database';
  enabled: boolean;
  configuration: Record<string, any>;
  credentials?: Record<string, any>;
  webhookUrl?: string;
  apiKey?: string;
}

export interface AssistantConfig {
  id: string;
  name: string;
  description?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  systemPrompt?: string;
  promptTemplate?: MessageEntry[];
  chatSettings?: ChatSettings;
  voiceEnabled?: boolean;
  voiceSettings?: VoiceSettings;
  tools?: ToolConfiguration[];
  integrations?: IntegrationConfig[];
  knowledgeBaseId?: string;
  calendars?: any[];
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

// ------------------------------
// Form schema & conditional deps
// ------------------------------

export type FieldOperator = 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'isEmpty' | 'isNotEmpty';

export interface FieldDependencyRule {
  field: keyof AssistantConfig | string;
  operator: FieldOperator;
  value?: any;
}

export interface FieldDependencyConfig {
  rules: FieldDependencyRule[];
  logic?: 'and' | 'or';
}

export interface FieldSchema {
  key: keyof AssistantConfig | string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'select' | 'checkbox' | 'json' | 'custom';
  required?: boolean;
  section?: 'basic' | 'advanced' | 'tools' | 'integrations';
  options?: Array<{ value: string; label: string }>; // for select
  dependsOn?: FieldDependencyConfig;
}

export interface AssistantFormSchema {
  fields: FieldSchema[];
}

export const defaultAssistantFormSchema: AssistantFormSchema = {
  fields: [
    { key: 'name', label: 'Assistant Name', type: 'text', required: true, section: 'basic' },
    { key: 'model', label: 'Model', type: 'select', required: true, section: 'basic', options: [
      // OpenAI Models (2025 Latest)
      { value: 'gpt-5', label: '🔥 GPT-5 (Latest 2025)' },
      { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
      { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
      { value: 'o3-mini', label: 'OpenAI o3-Mini (Reasoning)' },
      { value: 'o4-mini', label: 'OpenAI o4-Mini (Reasoning)' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
      { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
      { value: 'gpt-realtime', label: 'GPT Realtime (Audio)' },
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
      // Anthropic Models (2025 Latest)
      { value: 'claude-opus-4-1', label: '🔥 Claude Opus 4.1 (Latest 2025)' },
      { value: 'claude-opus-4', label: 'Claude Opus 4' },
      { value: 'claude-sonnet-4', label: 'Claude Sonnet 4' },
      { value: 'claude-3-7-sonnet', label: 'Claude 3.7 Sonnet (Hybrid)' },
      { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
      { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
      { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
      { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
      { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
      // Google Models (2025 Latest)
      { value: 'gemini-2.5-pro', label: '🔥 Gemini 2.5 Pro (Latest 2025)' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
      { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite' },
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
      { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash-Lite' },
      { value: 'gemini-2.0-pro-experimental', label: 'Gemini 2.0 Pro (Experimental)' },
      { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
      { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
      { value: 'gemini-1.0-pro', label: 'Gemini 1.0 Pro' },
    ] },
    { key: 'description', label: 'Description', type: 'textarea', section: 'basic' },
    { key: 'systemPrompt', label: 'System Prompt', type: 'textarea', required: true, section: 'basic' },
    { key: 'temperature', label: 'Temperature', type: 'number', section: 'advanced' },
    { key: 'maxTokens', label: 'Max Tokens', type: 'number', section: 'advanced' },
    { key: 'voiceEnabled', label: 'Enable Voice Capabilities', type: 'checkbox', section: 'advanced' },
    { key: 'voiceSettings.provider', label: 'Voice Provider', type: 'select', section: 'advanced',
      options: [
        { value: 'elevenlabs', label: 'ElevenLabs' },
        { value: 'openai', label: 'OpenAI TTS' },
        { value: 'azure', label: 'Azure Speech' },
        { value: 'google', label: 'Google Cloud TTS' },
      ],
      dependsOn: { rules: [{ field: 'voiceEnabled', operator: 'equals', value: true }], logic: 'and' }
    },
    { key: 'voiceSettings.voiceId', label: 'Voice ID', type: 'text', section: 'advanced',
      dependsOn: { rules: [{ field: 'voiceEnabled', operator: 'equals', value: true }], logic: 'and' }
    },
    { key: 'knowledgeBaseId', label: 'Knowledge Base', type: 'select', section: 'integrations', options: [
      { value: '', label: 'No knowledge base' },
      { value: 'kb-company', label: 'Company Knowledge Base' },
      { value: 'kb-product', label: 'Product Documentation' },
      { value: 'kb-support', label: 'Support Articles' },
      { value: 'kb-faq', label: 'FAQ Database' },
      { value: 'kb-custom', label: 'Custom Knowledge Base' }
    ] },
    { key: 'calendarEnabled', label: 'Enable Calendar Integration', type: 'checkbox', section: 'integrations' },
  ]
};

// ------------------------------
// Flow editor types (reactflow)
// ------------------------------

export type FlowNodeKind = 'start' | 'prompt' | 'tool' | 'decision' | 'end';

export interface FlowNodeData {
  label: string;
  promptText?: string;
  toolId?: string;
  decisionRule?: string;
}

export interface FlowNodeDefinition {
  id: string;
  type: FlowNodeKind;
  data: FlowNodeData;
}

export interface FlowEdgeDefinition {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface FlowDefinition {
  nodes: FlowNodeDefinition[];
  edges: FlowEdgeDefinition[];
}

export interface FlowValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateFlowDefinition = (flow: FlowDefinition): FlowValidationResult => {
  const errors: string[] = [];
  const nodeIds = new Set(flow.nodes.map(n => n.id));
  if (!flow.nodes.some(n => n.type === 'start')) errors.push('Flow must contain a start node');
  if (!flow.nodes.some(n => n.type === 'end')) errors.push('Flow must contain an end node');
  // Edges reference valid nodes
  flow.edges.forEach(e => {
    if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) errors.push(`Invalid edge ${e.id}: references unknown node`);
  });
  // Simple DAG check via DFS
  const adjacency: Record<string, string[]> = {};
  flow.edges.forEach(e => {
    adjacency[e.source] = adjacency[e.source] || [];
    adjacency[e.source].push(e.target);
  });
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const hasCycle = (id: string): boolean => {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    const next = adjacency[id] || [];
    for (const n of next) {
      if (hasCycle(n)) return true;
    }
    visiting.delete(id);
    visited.add(id);
    return false;
  };
  flow.nodes.forEach(n => {
    if (hasCycle(n.id)) errors.push('Flow must be a DAG (no cycles)');
  });
  return { isValid: errors.length === 0, errors };
};